import { FormField } from "@themes/pollinator/ui/FormField";
import { QuizFormField } from "../QuizFormField";
import {
  componentPropsFor,
  DESIGNED_KINDS,
  type PublicQuizQuestion as Question,
} from "./publicQuizQuestions";

export type Props = {
  /** The question, as publicQuizQuestionsFrom gives it */
  question: Question;
  /** Where the answer is held in the form; the question's own name unless it is held under a group */
  name?: string;
  /** The wording above the answer; the question's prompt unless a page words it otherwise */
  label?: string;
  /** Text under the label. A question of a designed kind shows none unless given it */
  helpText?: string;
  /** Drawn but not yet asked, as a question that waits on another answer is */
  disabled?: boolean;
  className?: string;
};

/**
 * One question of a quiz shown to people who are not signed in, drawn the
 * same way on every page that shows one: the intake forms and the quiz an
 * invitation asks before signup. Each page keeps its own layout around it.
 *
 * The six kinds the design draws go through the theme's form field
 * (`componentPropsFor`); anything else, a statement agreed to by going on
 * among them, is drawn as the platform's own quizzes draw it. Use it inside a
 * `Form`, whose form holds the answer under `name`.
 */
export function PublicQuizQuestion({
  question,
  name = question.name,
  label = question.label,
  helpText,
  disabled,
  className,
}: Props) {
  if (DESIGNED_KINDS.includes(question.kind)) {
    return (
      <FormField
        name={name}
        label={label}
        helpText={helpText}
        required={question.is_required}
        disabled={disabled}
        className={className}
        {...componentPropsFor(question)}
      />
    );
  }

  const { labelVariant, ...field } = question.field;
  return (
    <QuizFormField
      {...field}
      name={name}
      label={label}
      helpText={helpText ?? field.helpText}
      // An overline is too small to read a question by.
      labelVariant={labelVariant === "overline" ? "caption" : labelVariant}
      className={className}
    />
  );
}
